import React, { useState, useEffect } from 'react';
import { User, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { usePenNames } from '../hooks/usePenNames';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';

type Identity = {
  id: string;
  username: string;
  avatar_url: string | null;
  type: 'main' | 'pen';
};

interface IdentitySwitcherProps {
  isOpen: boolean;
  onToggle: () => void;
  position?: 'top' | 'bottom';
}

export default function IdentitySwitcher({ isOpen, onToggle, position = 'bottom' }: IdentitySwitcherProps) {
  const [currentIdentity, setCurrentIdentity] = useState<Identity | null>(null);
  const [identities, setIdentities] = useState<Identity[]>([]);
  const { penNames } = usePenNames();
  const { user } = useAuth();

  useEffect(() => {
    const fetchMainProfile = async () => {
      if (!user) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }

      if (profile) {
        const mainIdentity: Identity = {
          id: profile.id,
          username: profile.username,
          avatar_url: profile.avatar_url,
          type: 'main'
        };

        const penIdentities: Identity[] = penNames.map(pen => ({
          id: pen.id,
          username: pen.username,
          avatar_url: pen.avatar_url,
          type: 'pen'
        }));

        setIdentities([mainIdentity, ...penIdentities]);
        
        const savedId = localStorage.getItem('current_identity_id');
        const currentId = savedId || mainIdentity.id;
        const current = [mainIdentity, ...penIdentities].find(i => i.id === currentId) || mainIdentity;
        setCurrentIdentity(current);
      }
    };

    fetchMainProfile();
  }, [user, penNames]);

  if (!currentIdentity) return null;

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 p-1 rounded-lg hover:bg-gray-100 ${
          isOpen ? 'bg-gray-100' : ''
        }`}
      >
        {currentIdentity.avatar_url ? (
          <img
            src={currentIdentity.avatar_url}
            alt={currentIdentity.username}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <User className="w-5 h-5 text-gray-600" />
          </div>
        )}
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-gray-600" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-600" />
        )}
      </button>

      {isOpen && (
        <div className={`absolute ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 w-48 bg-white rounded-lg shadow-lg border z-50`}>
          {/* Add New Pen Name Button */}
          <Link
            to="/settings?showPenNameForm=true#pen-names"
            className="w-full px-4 py-2 text-left flex items-center gap-2 text-purple-600 hover:bg-gray-50 border-b"
            onClick={onToggle}
          >
            <Plus className="w-4 h-4" />
            <span>New Pen Name</span>
          </Link>

          {/* Identity List */}
          {identities.map(identity => (
            <button
              key={identity.id}
              onClick={() => {
                if (identity.type === 'main') {
                  localStorage.removeItem('current_identity_id');
                } else {
                  localStorage.setItem('current_identity_id', identity.id);
                }
                window.location.reload();
                onToggle();
              }}
              className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-50 ${
                currentIdentity.id === identity.id ? 'bg-gray-50' : ''
              }`}
            >
              {identity.avatar_url ? (
                <img
                  src={identity.avatar_url}
                  alt={identity.username}
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
              )}
              <div>
                <div className="font-medium text-gray-900">{identity.username}</div>
                <div className="text-xs text-gray-500">
                  {identity.type === 'main' ? 'Main Profile' : 'Pen Name'}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}